import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import styles from './BetPickTable.module.scss';
import { oddsDropBlock, RStatus, SCRRStatus, SCRStatus } from '@src/utils/kolBetpickUtils';
import {
    CombinationDataInter,
    HorseInfo,
    TableRow,
    TableRowQIN,
    TableRowQPL,
    TableRowTRIO,
} from '@src/utils/type-utils';

/** 用于betType为组合的样式 */
const BetPickTable = (props: CombinationDataInter) => {
    const { i18n, t } = useTranslation('kol');
    const lang = i18n.language.toLowerCase() === 'en-us';
    const { combinedData, betTypeName, displayOdds, refundData } = props;

    const header =
        betTypeName === 'QQP'
            ? [t('tableHorse'), t('Qin'), t('Qpl')]
            : [
                  t('tableHorse'),
                  betTypeName === 'QPL' ? t('Qpl') : betTypeName === 'TRIO' ? t('Trio') : t('Qin'),
              ];

    const renderHorseName = (horse: HorseInfo) => {
        const isSCR = SCRStatus.includes(horse.status);
        const isR = RStatus.includes(horse.status);
        const isSCRR = SCRRStatus.includes(horse.status);

        const prefix = isR || isSCRR ? `${t('betpick.r')}` : '';
        const name = lang ? horse.name_en : horse.name_ch;
        const suffix = isSCR || isSCRR ? ` (${t('betpick.scratched')})` : '';

        return `${horse.no}. ${prefix}${name}${suffix}`;
    };

    const renderOddsCell = (
        type: string,
        value: string,
        drop: number,
        hot: boolean,
        refund: boolean
    ) => {
        const isSCR = value === 'SCR';
        const noBG = isSCR || value === '---' || refund;
        const isTRIO = type === 'TRIO';

        return (
            <div
                className={`px-1 inline-block min-w-[30px] ${
                    !noBG && !isTRIO && oddsDropBlock(hot, drop)
                }`}
            >
                {refund ? '---' : isSCR ? t('betpick.scratched') : value}
            </div>
        );
    };

    return (
        <div className={styles.BetPickTable}>
            {/* 马匹列表 */}
            <div className='flex justify-between border-b border-[#E0E0E0] pb-[6px] text-[11px] font-normal text-black'>
                <div>{t('horse')}</div>
            </div>
            <div className='flex items-start flex-col'>
                {combinedData?.horseList?.map((horse: HorseInfo, index) => (
                    <div
                        key={index}
                        className='text-[13px] font-bold text-black mt-1 min-h-[20px] flex items-center'
                    >
                        {horse.isBankerROw && (
                            <div
                                className={`${
                                    index < 2 ? 'bg-[#003C84]' : ''
                                } text-white font-medium text-[10px] text-center min-w-[46px] h-[19px] leading-[19px] rounded-sm mr-2`}
                            >
                                {index === 0 && t('betpick.banker')}
                                {index === 1 && t('betpick.selection')}
                            </div>
                        )}
                        <div>{renderHorseName(horse)}</div>
                    </div>
                ))}
            </div>

            {/* 表格展示 */}
            {displayOdds && combinedData?.tableList?.length > 0 && (
                <table className='w-full text-[14px] mt-[12px]'>
                    <thead>
                        <tr>
                            {header.map((title, index) => (
                                <th
                                    key={index}
                                    className={cn(
                                        'text-left text-[11px] font-medium',
                                        index === 0 ? 'text-left' : 'text-center',
                                        header.length === 3 ? 'w-4/12' : 'w-2/4'
                                    )}
                                >
                                    <div>{title}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {combinedData?.tableList.map((row: TableRow, index) => (
                            <tr
                                key={index}
                                className='text-[13px] font-bold text-black pt-[5px] h-[20px]'
                            >
                                <td className='text-left'>{row.horsename}</td>

                                {betTypeName === 'QQP' ? (
                                    <>
                                        <td className='text-center text-[#333]'>
                                            {renderOddsCell(
                                                'QIN',
                                                (row as TableRowQIN).QINOddsValue,
                                                (row as TableRowQIN).QINOddsDropValue,
                                                (row as TableRowQIN).QINHotFavourite,
                                                refundData.QIN
                                            )}
                                        </td>
                                        <td className='text-center text-[#333]'>
                                            {renderOddsCell(
                                                'QPL',
                                                (row as TableRowQPL).QPLOddsValue,
                                                (row as TableRowQPL).QPLOddsDropValue,
                                                (row as TableRowQPL).QPLHotFavourite,
                                                refundData.QPL
                                            )}
                                        </td>
                                    </>
                                ) : betTypeName === 'QIN' ? (
                                    <td className='text-center text-[#333]'>
                                        {renderOddsCell(
                                            'QIN',
                                            (row as TableRowQIN).QINOddsValue,
                                            (row as TableRowQIN).QINOddsDropValue,
                                            (row as TableRowQIN).QINHotFavourite,
                                            refundData.QIN
                                        )}
                                    </td>
                                ) : betTypeName === 'TRIO' ? (
                                    <td className='text-center text-[#333]'>
                                        {renderOddsCell(
                                            'TRIO',
                                            (row as TableRowTRIO).TRIOOddsValue,
                                            (row as TableRowTRIO).TRIOOddsDropValue,
                                            (row as TableRowTRIO).TRIOHotFavourite,
                                            refundData.TRI
                                        )}
                                    </td>
                                ) : (
                                    <td className='text-center text-[#333]'>
                                        {renderOddsCell(
                                            'QPL',
                                            (row as TableRowQPL).QPLOddsValue,
                                            (row as TableRowQPL).QPLOddsDropValue,
                                            (row as TableRowQPL).QPLHotFavourite,
                                            refundData.QPL
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default memo(BetPickTable);
